import React, { Component } from "react"
import stateMutator from "../StateMutator"
import SketchyColorTypes from "./Types/SketchyColorTypes"
import SketchyColorPicker from "./SketchyColorPicker/SketchyColorPicker"
import sketchyColors from "./Utils/SketchyColors"
import SketchyEraser from "./Utils/SVG/SketchyEraser"
import SketchyPenPicker from "./SketchyPenPicker/SketchyPenPicker"
import sketchyPenSizes from "./Utils/SketchyPenSizes"
import SketchyPenPickerTypes from "./Types/SketchyPenPickerTypes"
import sketchyTooltip from "./Utils/SketchyTooltip"
import { OverlayTrigger } from "react-bootstrap"
import uuid from "react-uuid"
import "./Sketchy.css"

interface Props {
    manageParentState: Function
    handleChange: Function
    row: number
    col: number
    value: string | null
    configField?: boolean
    templateField?: boolean
    readOnly?: boolean
}

interface State {
    isDrawing: boolean
    isErasing: boolean
    imageUrl: string | null
    color: SketchyColorTypes
    pen: SketchyPenPickerTypes
}

class Sketchy extends Component<Props, State> {
    private canvasRef: React.RefObject<HTMLCanvasElement>
    private ctx: CanvasRenderingContext2D | null = null
    private sscId: string = ""
    private canvasID: string = ""
    private imgId: string = ""
    private fileInputId: string = ""
    constructor(props: Props) {
        super(props)
        this.canvasRef = React.createRef()
        this.state = {
            isDrawing: false,
            isErasing: false,
            imageUrl: null,
            color: sketchyColors[0],
            pen: sketchyPenSizes[0]

        }
        //@ts-ignore
        this.export = this.debounce(this.export.bind(this), 1000)
    }

    componentDidMount() {
        const { templateField } = this.props
        this.sscId = uuid()
        this.canvasID = uuid()
        this.imgId = uuid()
        this.fileInputId = uuid()
        const { value } = this.props
        const canvas = this.canvasRef.current
        if (canvas) {
            this.ctx = canvas.getContext("2d")
        }
        if (value) {
            const base64Data = value
            const mimeType = "image/png"
            const fullBase64String = `data:${mimeType};base64,${base64Data}`
            this.handleBase64Image(fullBase64String)
        }
        if (templateField) {
            this.resizeHandler()
        }
    }

    manageState = (keys: Array<{ key: string, value?: any }>) => {
        this.setState(stateMutator.mutate(this.state, keys))
    }

    startDrawing = (e: React.MouseEvent) => {
        const rect = this.canvasRef.current?.getBoundingClientRect()
        const mouseX = e.clientX - (rect ? rect.left : 0)
        const mouseY = e.clientY - (rect ? rect.top : 0)

        if (this.ctx) {
            this.ctx.beginPath() // Start a new path
            this.ctx.moveTo(mouseX, mouseY) // Move to the starting point
        }

        this.manageState([{ key: "isDrawing", value: true }])
    }

    stopDrawing = () => {
        this.manageState([{ key: "isDrawing", value: false }])
        this.export()
    }

    draw = (e: React.MouseEvent) => {
        const { configField, readOnly } = this.props
        if (!this.state.isDrawing || !this.ctx || readOnly || configField) return

        const rect = this.canvasRef.current?.getBoundingClientRect()
        const mouseX = e.clientX - (rect ? rect.left : 0)
        const mouseY = e.clientY - (rect ? rect.top : 0)

        // Set drawing or erasing styles
        this.ctx.globalCompositeOperation = this.state.isErasing ? "destination-out" : "source-over"
        this.ctx.lineWidth = this.state.pen.size
        this.ctx.strokeStyle = this.state.isErasing ? "#00FFFFFF" : this.state.color.hc

        this.ctx.lineTo(mouseX, mouseY) // Draw a line to the new position
        this.ctx.stroke() // Render the line

        this.ctx.beginPath() // Begin a new path to avoid drawing lines from the last point on mouse move
        this.ctx.moveTo(mouseX, mouseY) // Move to the current position
    }


    exportCombinedToBase64 = async () => {
        return new Promise((resolve, reject) => {
            const image = new Image()
            const { imageUrl } = this.state

            if (!imageUrl) {
                reject("No image URL available.")
                return
            }

            image.onload = () => {
                const canvas = document.createElement('canvas')
                const ctx = canvas.getContext('2d')
                if (!ctx) {
                    reject("Unable to get canvas context.")
                    return
                }

                const parentWidth = this.canvasRef.current?.parentElement?.offsetWidth ?? image.width
                const imgAspectRatio = image.height / image.width
                let newCanvasWidth = image.width
                let newCanvasHeight = image.height

                if (image.width > parentWidth) {
                    newCanvasWidth = parentWidth
                    newCanvasHeight = parentWidth * imgAspectRatio
                }

                canvas.width = newCanvasWidth
                canvas.height = newCanvasHeight

                ctx.drawImage(image, 0, 0, newCanvasWidth, newCanvasHeight)

                const drawingCanvas = this.canvasRef.current
                if (drawingCanvas && this.ctx) {
                    ctx.drawImage(drawingCanvas, 0, 0, newCanvasWidth, newCanvasHeight)
                }

                const base64Image = canvas.toDataURL("image/png")
                resolve(base64Image)
            }

            image.onerror = (error) => {
                reject("Error loading image: " + error)
            }

            image.src = imageUrl
        })
    }

    handleBase64Image = (base64String: string) => {
        // Convert the base64 string to an image URL and update the state
        this.manageState([{ key: "imageUrl", value: base64String }])
        const img = new Image()
        img.onload = () => {
            if (this.ctx) {
                const canvas = this.canvasRef.current
                if (canvas && canvas.parentElement) {
                    const imgAspectRatio = img.height / img.width
                    let newCanvasWidth = img.width
                    let newCanvasHeight = img.height

                    const parentWidth = canvas.parentElement.offsetWidth
                    if (img.width > parentWidth) {
                        newCanvasWidth = parentWidth
                        newCanvasHeight = parentWidth * imgAspectRatio
                    }

                    canvas.width = newCanvasWidth
                    canvas.height = newCanvasHeight

                    this.ctx.clearRect(0, 0, newCanvasWidth, newCanvasHeight)
                }
            }
        }
        img.src = base64String
        this.export()
    }

    handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0]
            const reader = new FileReader()

            reader.onload = (e) => {
                const imageUrl = e.target!.result as string
                this.manageState([{ key: "imageUrl", value: imageUrl }])

                const img = new Image()
                img.onload = () => {
                    if (this.ctx) {
                        const canvas = this.canvasRef.current
                        if (canvas && canvas.parentElement) {
                            const imgAspectRatio = img.height / img.width
                            let newCanvasWidth = img.width
                            let newCanvasHeight = img.height

                            const parentWidth = canvas.parentElement.offsetWidth
                            if (img.width > parentWidth) {
                                newCanvasWidth = parentWidth
                                newCanvasHeight = parentWidth * imgAspectRatio
                            }

                            canvas.width = newCanvasWidth
                            canvas.height = newCanvasHeight

                            const imgElem: any = document.getElementById(this.imgId)
                            imgElem.style.width = `${newCanvasWidth}px`
                            imgElem.style.height = `${newCanvasHeight}px`

                            this.ctx.clearRect(0, 0, newCanvasWidth, newCanvasHeight)
                        }
                    }
                }
                img.src = imageUrl
            }

            reader.readAsDataURL(file)
            this.export()
        }
    }


    clearCanvas = () => {
        const canvas = this.canvasRef.current
        const ctx = this.ctx
        this.manageState([{ key: "isDrawing", value: false }, { key: "isErasing", value: false }])
        if (canvas && ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height)
        }
    }


    debounce = (func: (arg0: any) => void, wait: number | undefined) => {
        let timeout: string | number | NodeJS.Timeout | undefined
        return function executedFunction(...args: any[]) {
            const later = () => {
                clearTimeout(timeout)
                //@ts-ignore
                func(...args)
            }
            clearTimeout(timeout)
            timeout = setTimeout(later, wait)
        }
    }

    export = async () => {
        const { handleChange, manageParentState, row, col } = this.props
        manageParentState([{ key: "target", value: { row: row, col: col, key: "Value" } }])
        try {
            const base64String = await this.exportCombinedToBase64()
            // console.log(base64String)
            //@ts-ignore
            const cleanBase64 = base64String.replace("data:image/png;base64,", "")
            handleChange({ target: { value: cleanBase64 } })
        } catch (error) {
            console.error("Error exporting canvas and image:", error)
        }
    }

    resizeHandler = () => {
        const { imageUrl } = this.state
        const scc: HTMLElement | null = document.getElementById(this.sscId)
        const img = document.getElementById(this.imgId)
        const canvas = this.canvasRef.current

        if (imageUrl && scc && canvas && canvas.parentElement && img && this.ctx) {
            const sccWidth = scc.clientWidth
            const canvasWidth = canvas.width
            if (sccWidth !== canvasWidth) {
                console.log("???image resized")
                const imgAspectRatio = scc.clientHeight / scc.clientWidth
                let newCanvasWidth = scc.clientWidth
                let newCanvasHeight = scc.clientHeight
                const parentWidth = canvas.parentElement.offsetWidth
                if (scc.clientWidth > parentWidth) {
                    newCanvasWidth = parentWidth
                    newCanvasHeight = parentWidth * imgAspectRatio
                }
                canvas.width = newCanvasWidth
                canvas.height = newCanvasHeight
                const imgElem: any = document.getElementById(this.imgId)
                imgElem.style.width = `${newCanvasWidth}px`
                imgElem.style.height = `${newCanvasHeight}px`
                img.style.objectFit = "contain"
                this.ctx.clearRect(0, 0, newCanvasWidth, newCanvasHeight)
            }
        }
        setTimeout(() => {
            this.resizeHandler()
        }, 500)
    }

    render() {

        const { manageParentState, row, col, value, configField, readOnly } = this.props
        const { imageUrl, isErasing, color, pen } = this.state

        // console.log(this.props)

        return (
            <div className="Sketchy">
                {(!readOnly && !configField) && <div className="SketchyControlsUpper">
                    <div className="SketchyControlsUpperLeft">
                        <OverlayTrigger placement="top" overlay={sketchyTooltip("Choose photo")}>
                            <div className="SketchyControlsUpperItem">
                                <div className="SketchyFileUploadBtn">
                                    <label htmlFor={this.fileInputId}>
                                        <span className="material-icons">file_upload</span>
                                    </label>
                                    <input
                                        type="file"
                                        id={this.fileInputId}
                                        onChange={this.handleImageUpload}
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                    />
                                </div>
                            </div>
                        </OverlayTrigger>
                        {value && <OverlayTrigger placement="top" overlay={sketchyTooltip("Clear all")}>
                            <div className="SketchyControlsUpperItem" onClick={this.clearCanvas}>
                                <span className="material-icons-outlined">clear</span>
                            </div>
                        </OverlayTrigger>}
                    </div>
                    {imageUrl && <div className="SketchyControlsUpperRight">
                        <OverlayTrigger placement="top" overlay={sketchyTooltip("Toggle pen")}>
                            <div className={`SketchyControlsUpperItem ${!isErasing ? "SketchyControlsUpperItemChecked" : "SketchyControlsUpperItemNotChecked"}`} onClick={() => this.manageState([{ key: "isErasing", value: false }])}>
                                <span className="material-icons-outlined">draw</span>
                            </div>
                        </OverlayTrigger>
                        <OverlayTrigger placement="top" overlay={sketchyTooltip("Toggle eraser")}>
                            <div className={`SketchyControlsUpperItem ${isErasing ? "SketchyControlsUpperItemChecked" : "SketchyControlsUpperItemNotChecked"}`} onClick={() => this.manageState([{ key: "isErasing", value: true }])}>
                                <SketchyEraser />
                            </div>
                        </OverlayTrigger>
                        <div className="SketchyControlsUpperItemDivider"></div>
                        <SketchyPenPicker
                            manageParentState={this.manageState}
                            pen={pen}
                            color={color}
                            isErasing={isErasing}
                        />
                        <div className="SketchyControlsUpperItemDivider"></div>
                        <SketchyColorPicker
                            manageParentState={this.manageState}
                            color={color}
                        />
                    </div>}

                </div>}
                <div className="SketchyCanvasContainer"
                    id={this.sscId}
                    onMouseEnter={() => manageParentState([{ key: "target", value: { row: row, col: col, key: "Value" } }])}
                >
                    <canvas
                        id={this.canvasID}
                        ref={this.canvasRef}
                        onMouseDown={this.startDrawing}
                        onMouseMove={this.draw}
                        onMouseUp={this.stopDrawing}
                        onMouseLeave={this.stopDrawing}
                        style={{ display: `${imageUrl ? "block" : "none"}`, zIndex: 1 }}
                    />
                    <img id={this.imgId} src={`${imageUrl}`} alt="" style={{ position: "absolute", zIndex: 0 }}></img>
                </div>
                <div className="SketchyControlsLower">
                    <div className="SketchyControlsLowerLeft">

                    </div>
                </div>
            </div>
        )
    }
}

export default Sketchy
